import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Div, Headline, Flex, Gutter, SearchInput, Button, Loader, ModalDialog, BodyText } from '@gaiads/telia-react-component-library';
import { useHistory } from 'react-router-dom';

import { selectAllLocales } from '../../store/locales';
import { fetchSalesChannels } from '../../store/salesChannels';
import BottomDrawer from '../../shared/ListUtil/BottomDrawer';
import { Loading } from '../../utils/store';
import {
  selectAdminBundlesLoading,
  selectAdminBundlesPageMetadata,
  fetchAdminBundles,
  selectAllAdminBundlesWithContent,
  deleteBundles,
  fetchBundleContent,
} from '../../store/adminBundles';
import BundlesList from './BundlesList/BundlesList';
import SalesChannelDrawer from './SalesChannelDrawer/SalesChannelDrawer';
import { ListPagination } from '../../shared/ListUtil/Pagination';

import './Bundles.scss';

export default function BundlesView() {
  const { t } = useTranslation('bundlesview');
  const dispatch = useDispatch();
  const history = useHistory();
  const bundleListItems = useSelector(selectAllAdminBundlesWithContent);
  const loading = useSelector(selectAdminBundlesLoading) !== Loading.SUCCEEDED;
  const pageMetadata = useSelector(selectAdminBundlesPageMetadata);
  const locales = useSelector(selectAllLocales);

  const numberOfItemsPerPageOptions = [20, 50, 100];

  const [searchValue, setSearchValue] = useState<string>('');
  const [activeSearchText, setActiveSearchText] = useState<string | undefined>(undefined);
  const [selectedBundles, setSelectedBundles] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [openedBundles, setOpenedBundles] = useState<number[]>([]);
  const [loadedBundles, setLoadedBundles] = useState<number[]>([]);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
  const [numberOfItems, setNumberOfItems] = useState<number>(numberOfItemsPerPageOptions[0]);
  const [deleteConfirmationBundles, setDeleteConfirmationBundles] = useState<boolean>(false);
  const [showSalesChannelDrawer, setShowSalesChannelDrawer] = useState<boolean>(false);

  useEffect(() => {
    setLoadedBundles([]);
    setOpenedBundles([]);
    dispatch(fetchSalesChannels());
    dispatch(
      fetchAdminBundles({
        goToPageNumber: currentPageNumber,
        numberOfItems: numberOfItems,
        searchText: activeSearchText,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageNumber, numberOfItems, activeSearchText]);

  const onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void = event => {
    setSearchValue(event.target.value);
  };

  const onSearchSubmit = () => {
    const normalizedSearchText = searchValue?.trim() || undefined;
    const isNoStateChange = currentPageNumber === 0 && normalizedSearchText === activeSearchText;

    setCurrentPageNumber(0);
    setActiveSearchText(normalizedSearchText);

    if (isNoStateChange) {
      dispatch(
        fetchAdminBundles({
          goToPageNumber: 0,
          numberOfItems: numberOfItems,
          searchText: normalizedSearchText,
        }),
      );
    }
  };

  const onSearchKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void = event => {
    if (event.key === 'Enter') {
      onSearchSubmit();
    }
  };

  const handleDeleteBundles = async () => {
    setDeleteConfirmationBundles(false);
    if (selectedBundles.length === 0) {
      return;
    }

    dispatch(deleteBundles(selectedBundles));
  };

  const resetSelections = () => {
    setSelectedBundles([]);
    setSelectAll(false);
  };

  /* Checkbox event handlers */

  const handleAllChecked = () => {
    const updatedSelectAll = !selectAll;
    let updatedSelection: number[] = [];

    if (updatedSelectAll) {
      const bundleListItemsIds = bundleListItems.map(item => item.id);
      updatedSelection = [...bundleListItemsIds];
    }

    setSelectAll(updatedSelectAll);
    setSelectedBundles(updatedSelection);
  };

  const isSelected = (bundleId: number): boolean => selectedBundles.some(existingBundleId => existingBundleId === bundleId);
  const isOpened = (bundleId: number): boolean => openedBundles.some(existingBundleId => existingBundleId === bundleId);
  const isLoaded = (bundleId: number): boolean => loadedBundles.some(existingBundleId => existingBundleId === bundleId);

  const handleBundleChecked = (bundleId: number) => {
    let updateSelected = [...selectedBundles];
    if (isSelected(bundleId)) {
      updateSelected = updateSelected.filter(selectedBundleId => selectedBundleId !== bundleId);
    } else {
      updateSelected.push(bundleId);
    }
    setSelectedBundles(updateSelected);
  };

  // Move to bundle detail view
  const moveToBundleDetail = (bundleId: number) => {
    history.push(`/bundle/${bundleId}`);
  };

  // Open or close product's variants section
  const handleOpenRow = async (bundleId: number) => {
    let updateOpened = [...openedBundles];
    if (isOpened(bundleId)) {
      updateOpened = updateOpened.filter(openBundleId => openBundleId !== bundleId);
    } else {
      updateOpened.push(bundleId);
    }
    setOpenedBundles(updateOpened);

    if (!isLoaded(bundleId)) {
      await dispatch(fetchBundleContent(bundleId));
      setLoadedBundles(previousLoadedBundles => [...previousLoadedBundles, bundleId]);
    }
  };

  const handleSalesChannelCloseClick = () => {
    setShowSalesChannelDrawer(false);
  };
  const handleAddToSalesChannelClick = () => {
    setShowSalesChannelDrawer(true);
  };
  const selectedBundlesCount = selectedBundles.length;
  const selectedBundlesWithObjects = bundleListItems.filter(bundle => selectedBundles.includes(bundle.id));

  return (
    <Div margin={{ horizontal: 'xxlg', vertical: 'xlg' }}>
      <Flex spaceBetween>
        <Headline size={'md'} padding={{ horizontal: 'xxxs' }}>
          {t('header_title')}
        </Headline>
        <Button variant="primary" size="md" onClick={() => history.push('/bundle')}>
          {`+ ${t('add_bundle')}`}
        </Button>
      </Flex>
      <Gutter size={'xlg'} />
      <Div>
        <Flex>
          <Div flexItem={{ sizeMultiplier: 2, alignSelfTo: 'bottom' }} padding={{ horizontal: 'xxxs' }}>
            <SearchInput
              placeholder={t('filter.textsearchplaceholder')}
              value={searchValue}
              onChange={onSearchChange}
              onIconClick={onSearchSubmit}
              onKeyDown={onSearchKeyDown}
            />
          </Div>
          <Div flexItem={{ sizeMultiplier: 1 }} padding={{ horizontal: 'md' }}></Div>
          <Div flexItem={{ sizeMultiplier: 4, alignSelfTo: 'bottom' }} padding={{ horizontal: 'xxxs' }}></Div>
        </Flex>
        <Gutter size="xlg" />
        {showSalesChannelDrawer && selectedBundles.length > 0 && (
          <SalesChannelDrawer selectedItems={selectedBundlesWithObjects} onCloseClick={handleSalesChannelCloseClick} />
        )}
        <Div padding={{ horizontal: 'xxxs' }}>
          {loading ? (
            <Div alignTextToCenter padding={{ top: 'xlg' }}>
              <Loader size="lg" />
            </Div>
          ) : (
            <BundlesList
              bundleListItems={bundleListItems}
              locales={locales}
              selectAll={selectAll}
              onBundleChecked={handleBundleChecked}
              onAllChecked={handleAllChecked}
              isOpened={isOpened}
              isSelected={isSelected}
              isLoaded={isLoaded}
              onClickRow={moveToBundleDetail}
              onOpenRow={handleOpenRow}
            />
          )}
        </Div>
        <Gutter size="lg" />
        {!loading && (
          <ListPagination
            pageMetadata={pageMetadata}
            showText={t('pagination.show')}
            numberOfItemsPerPageOptions={numberOfItemsPerPageOptions}
            currentPageNumber={currentPageNumber}
            onPageNumberChange={setCurrentPageNumber}
            numberOfItems={numberOfItems}
            onNumberOfItemsChange={setNumberOfItems}
          />
        )}
      </Div>
      {selectedBundles.length > 0 && (
        <BottomDrawer<number>
          selectedItems={selectedBundles}
          handleEditClick={moveToBundleDetail}
          handleDeleteClick={() => setDeleteConfirmationBundles(true)}
          handleCancelClick={resetSelections}
          editButtonText={t('button.edit_bundle')}
          selectedText={t('selected.bundleSelected', { count: selectedBundlesCount })}
          handleAddClick={() => handleAddToSalesChannelClick()}
        />
      )}
      <ModalDialog
        title={t('delete_bundle_confirmation.title', { count: selectedBundlesCount })}
        closeButtonText={t('delete_bundle_confirmation.buttons.cancel')}
        acceptButtonText={t('delete_bundle_confirmation.buttons.delete')}
        isOpen={deleteConfirmationBundles}
        onCloseClick={() => setDeleteConfirmationBundles(false)}
        onAcceptClick={() => handleDeleteBundles()}
      >
        <BodyText>{t('delete_bundle_confirmation.content', { count: selectedBundlesCount })}</BodyText>
      </ModalDialog>
    </Div>
  );
}
